import { useHookstate, type State } from "@hookstate/core";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, ListItem, ListItemText, Typography } from "@mui/material";
import React, { useEffect, useState } from "react"
import { useLang } from "~/hooks/useLang";
import type { IWordCloudOccurrence } from "../../interface";
import dayjs from "dayjs";
import InfiniteScroll from "react-infinite-scroll-component";
import useSystemState from "~/features/System/stores/SystemState";
import useWordCloudState from "~/features/Management/stores/WordCloudState";
import useDispatchState from "~/features/Dispatch/stores/DispatchState";

interface ResultSearchWordCloudProps {
  readonly reloadList: State<boolean>;
} 


// eslint-disable-next-line max-lines-per-function
export default function ResultSearchWordCloud({ reloadList }: ResultSearchWordCloudProps) {
  const { translate } = useLang();
  const { getStatusColors } = useSystemState();
  const { occurrencesWord } = useWordCloudState();
  const { syncDispatchFromId } = useDispatchState();
  const [displayedOccurrences, setDisplayedOccurrences] = useState<readonly IWordCloudOccurrence[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const stationHasAccess = useHookstate(false)
  const open = useHookstate(false);
  const dispatchCode = useHookstate<string | null>(null);
  const dispatchId = useHookstate<number | null>(null);
  const itemsPerPage = 10;
  const rawOccurrences = occurrencesWord().get()
  const allOccurrences: readonly IWordCloudOccurrence[] = Array.isArray(rawOccurrences)
    ? JSON.parse(JSON.stringify(rawOccurrences))
    : []
  const hasMore = displayedOccurrences.length < allOccurrences.length;

  useEffect(() => {
    setCurrentPage(0);
    setDisplayedOccurrences(allOccurrences.slice(0, itemsPerPage));
  }, [reloadList.get()]);


  const handleClickOpen = ({ id, code, HasAccess }) => {
    dispatchCode.set(code)
    dispatchId.set(id)
    stationHasAccess.set(HasAccess)
    open.set(true)
  }

  const loadMoreOccurrences = () => {
    const nextPage = currentPage + 1;
    const endIndex = (nextPage + 1) * itemsPerPage;
    const newData = allOccurrences.slice(0, endIndex);

    setDisplayedOccurrences([...newData]);
    setCurrentPage(nextPage);
  };

  const handleClose = () => {
    open.set(false)
  }

  const handleChangeDispatch = () => {
    handleClose()
    syncDispatchFromId(dispatchId.get())
  }
  return (
    <>
      <Grid container xs={12}>
        <Grid item xs={12}>
          <InfiniteScroll
            dataLength={displayedOccurrences.length}
            next={loadMoreOccurrences}
            hasMore={hasMore}
            height={400}
            loader={<CircularProgress />}
          >
            {displayedOccurrences.length > 0 ? (
              displayedOccurrences.map((dispatch) => {
                const dispatches = Array.isArray(dispatch?.dispatches)
                  ? dispatch?.dispatches?.filter(Boolean)
                  : []
                const dispatchCodes = dispatches
                  .map((item) => item?.code)
                  .filter(Boolean)
                  .join(', ')

                return (
                <React.Fragment key={dispatch.id}>
                  <ListItem sx={{ width: '100%', margin: 0, padding: 0, display: 'flex', alignItems: 'flex-start', flexDirection: 'column', justifyContent: 'flex-start', gap: 2 }}>
                    <ListItemText
                      primary={`OC: ${dispatch?.id} | ${dispatchCodes}`}
                      secondary={
                        <>
                          <Typography variant={'subtitle2'}>
                            {`${dispatch?.type?.name} - ${dispatch?.subtype ? dispatch?.subtype.name : ''}`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {dispatch?.location}
                          </Typography>
                          {dispatch.name &&
                            <Typography variant={'subtitle2'}>
                              {`${translate('Requester')}: ${dispatch?.name} - ${dispatch?.phone ? dispatch?.phone : translate('Telephone number not provided')}`}
                            </Typography>
                          }
                          <Typography variant={'subtitle2'}>
                            {`${translate('Entry Origin')}: ${dispatch?.entry_origin?.name}`}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            Data da Ocorrência:
                            {' '}
                            {dayjs(dispatch.created_at).format('DD/MM/YYYY HH:mm')}
                          </Typography>
                        </>
                      }
                    />
                    <Grid sx={{ width: '100%', display: 'flex', gap: 1 }}>
                      {dispatches.map((element) => (
                        <Chip
                          key={element.id}
                          label={element.code}
                          onClick={() => handleClickOpen({ id: element.id, code: element.code, HasAccess: element.stationHasAccess })}
                          sx={{
                            backgroundColor: getStatusColors(element.status?.id).main,
                            color: getStatusColors(element.status?.id).contrastText,
                            '&:hover': {
                              backgroundColor: getStatusColors(element.status?.id).dark,
                              color: getStatusColors(element.status?.id).contrastText,
                            },
                          }}
                        />
                      ))}
                    </Grid>
                  </ListItem>
                  <Divider sx={{ marginTop: 2 }} />
                </React.Fragment>
              )})
            ) : (
              <Grid xs={12} style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography>
                  {translate('No information')}
                </Typography>
              </Grid>
            )}
          </InfiniteScroll>
        </Grid>
      </Grid>

      <Dialog maxWidth={'xs'} fullWidth={true} open={open.get()} onClose={handleClose}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {!stationHasAccess.get() ?
            <Typography variant='body2'>
              {translate('The station does not have access to the dispatch', { dispatch: dispatchCode.get() })}
            </Typography> :
            dispatchCode.get() && stationHasAccess.get() ? (
              <Typography variant='body2'>
                {translate('Do you want to open the dispatch', { dispatch: dispatchCode.get() })}
              </Typography>
            ) : (
              false
            )}
        </DialogContent>
        <DialogActions>
          {!stationHasAccess.get() ?
            <Button variant="contained" onClick={handleClose}>
              {translate('Close')}
            </Button> :
            <>
              <Button variant="outlined" onClick={handleClose}>
                {translate('Cancel')}
              </Button>
              <Button
                disabled={!stationHasAccess.get()}
                variant="contained"
                onClick={handleChangeDispatch}>
                {translate('Yes')}
              </Button>
            </>
          }
        </DialogActions>
      </Dialog>
    </>

  )
}
